// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import * as Sentry from "@sentry/react"

// M7 — Frontend observability (Sentry errors + RUM + backend-trace link).
//
// The browser ships to Sentry only (a purpose-built client SaaS).
// It NEVER talks to the central cloud log store and holds no
// cloud-store credential — see ADR-0041 (trust boundary for
// client-originated telemetry) and ADR-0039 (Sentry for the frontend,
// linked to the backend trace via the W3C `traceparent`).
//
// Three JS error layers are captured:
//   1. unhandled errors        — Sentry's `window.onerror` global handler
//   2. unhandled rejections    — Sentry's `window.onunhandledrejection`
//   3. React render errors     — the exported `SentryErrorBoundary`
//
// Both global handlers are installed by `Sentry.init` (the
// `GlobalHandlers` integration ships in the default integration set),
// so a single `init` call wires layers 1 and 2.
export const SentryErrorBoundary = Sentry.ErrorBoundary

export type InitObservabilityOptions = {
  // When absent/empty, observability stays disabled (no-op Sentry.init).
  dsn?: string
  environment?: string
  // Used by Sentry to match uploaded source maps to this build.
  release?: string
  // Defaults to 1.0 (full sampling) for RUM/Web Vitals coverage.
  tracesSampleRate?: number
}

// Called once before React mounts; missing DSN short-circuits so
// non-production builds never ship events to Sentry.
// `browserTracingIntegration` is the RUM source: it records Core Web
// Vitals (LCP/CLS/INP/FID) and produces the active trace whose id is
// propagated to the Backend as a W3C `traceparent` (see
// `buildTraceparent`), so a captured browser error links to its
// backend trace (ADR-0039).
export function initObservability(options: InitObservabilityOptions): void {
  if (!options.dsn) {
    return
  }

  Sentry.init({
    dsn: options.dsn,
    environment: options.environment,
    release: options.release,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: options.tracesSampleRate ?? 1.0,
  })
}

// Translates Sentry's `sentry-trace` token (<traceId>-<spanId>[-<sampled>])
// into the W3C `traceparent` format (00-<traceId>-<spanId>-<flags>).
// The trace id passes through unchanged so the Backend (ADR-0039, AC5)
// ends up on the same trace.id as the browser error Sentry captured —
// that is the cross-stack link.
// Returns undefined when no active trace exists; callers omit the header.
export function buildTraceparent(): string | undefined {
  const sentryTrace = Sentry.getTraceData()["sentry-trace"]
  if (!sentryTrace) {
    return undefined
  }

  const [traceId, spanId, sampled] = sentryTrace.split("-")
  if (!traceId || !spanId) {
    return undefined
  }

  // `sampled` is "1"/"0"/undefined in the sentry-trace token; W3C flags
  // is a 2-hex byte whose 0x01 bit is the sampled flag. Default to
  // sampled when the token omits it (Sentry treats a bare token as
  // part of a sampled trace).
  const flags = sampled === "0" ? "00" : "01"
  return `00-${traceId}-${spanId}-${flags}`
}

export type ApiErrorContext = {
  url?: string
  method?: string
  status?: number
}

// Wired into the shared axios response interceptor so silent network
// failures become visible in Sentry.
export function captureApiError(error: unknown, context: ApiErrorContext): void {
  Sentry.withScope((scope) => {
    scope.setContext("api_call", {
      url: context.url ?? null,
      method: context.method ?? null,
      status: context.status ?? null,
    })
    Sentry.captureException(error)
  })
}
